<template>
  <div>
    <Head title="Agenda" />
    <div class="shadow_box rounded-lg p-6">
      <h1 class="mb-6 text-2xl text-gray-777 font-roboto uppercase">Agenda Geral</h1>

      <div class="flex flex-wrap max-w-7xl m-auto my-0">
        <div class="mb-5 lg:mb-10 w-full lg:w-5/12 lg:pr-6">
          <calendar v-model="selectedDate" :dottedDates="dottedDates" :handleMonthYear="handleMonthYear" />
        </div>
        <div class="font-roboto mb-10 w-full lg:w-7/12 flex flex-wrap items-center">
          <div class="w-full lg:w-1/2">
            <div class="bg-blue-600 text-white rounded-lg p-5 lg:mx-5">
              <div class="font-bold text-3xl">{{ totalMonth }}</div>
              <div class="font-light">Agendamentos no mês</div>
            </div>
          </div>
          <div class="w-full lg:w-1/2">
            <div v-if="selectedDate" class="bg-blue-600 text-white rounded-lg p-5 mt-5 lg:mt-0 lg:mx-5">
              <div class="font-bold text-3xl">{{ totalDay }}</div>
              <div class="font-light">Agendamentos no dia</div>
            </div>
          </div>
        </div>
      </div>

      <data-table
        v-if="loadTable"
        v-model:columns="columns"
        route="appointments/list"
        :query='{ "date": selectedDateFormatted, "month": selectedMonthFormatted }'
        no-data-label="Nenhum agendamento cadastrado"
        no-result-label="Nenhum agendamento encontrado"
      >
        <template #table.cell.content.actions="{ value, row }">
          <div class="inline-grid grid-cols-2 gap-4" :class="{'grid-cols-3': row.openRoom && user.role == 2 && user.professional.id == row.professional.id}">
            <span v-if="row.openRoom && user.role == 2 && user.professional.id == row.professional.id" @click="openRoom(row)"><font-awesome-icon icon="fa-video" class="text-gray-500 text-lg cursor-pointer" /></span>
            <span @click="openModalEdit(row)"><font-awesome-icon icon="fa-pen-to-square" class="text-blue-500 text-lg cursor-pointer" /></span>
            <span @click="openModalConfirmDelete(row)"><font-awesome-icon icon="fa-trash-can" class="text-red-600 text-lg cursor-pointer" /></span>
          </div>
        </template>
        <template #grid.content.body.actions="{ value, row }">
          <div class="inline-grid grid-cols-2 gap-4" :class="{'grid-cols-3': row.openRoom && user.role == 2 && user.professional.id == row.professional.id}">
            <span v-if="row.openRoom && user.role == 2 && user.professional.id == row.professional.id" @click="openRoom(row)"><font-awesome-icon icon="fa-video" class="text-gray-500 text-lg cursor-pointer" /></span>
            <span @click="openModalEdit(row)"><font-awesome-icon icon="fa-pen-to-square" class="text-blue-500 text-lg cursor-pointer" /></span>
            <span @click="openModalConfirmDelete(row)"><font-awesome-icon icon="fa-trash-can" class="text-red-600 text-lg cursor-pointer" /></span>
          </div>
        </template>

        <template #after.reload-button>
          <button class="flex items-end font-roboto font-medium btn-blue uppercase" @click.prevent="openModalEdit({})">
            <font-awesome-icon icon="fa-plus" class="text-white text-lg mr-0 lg:mr-2" /><span class="hidden lg:block"> Adicionar Agendamento</span>
          </button>
        </template>
      </data-table>
    </div>
    <modal-delete ref="modalDelete" @confirm="reloadTable"></modal-delete>
    <modal-edit ref="modalEdit" title="Agendamento" url="appointments" @submitForm="reloadTable" @submitError="submitError" :item="item">
      <template #form="{item}">
        <vue-select-input v-model="item.specialty" :options="specialtiesFiltered" :error="item.errors[`specialty.id`]" class="lg:pr-6 w-full lg:w-1/2" label="Especialidade" text="nenhuma especialidade encontrada" />
        
        <vue-select-input v-if="user.role == 1" v-model="item.group" :options="groupsFiltered" :error="item.errors[`group.id`]" class="mt-6 lg:mt-0 w-full lg:w-1/2" label="Grupo" text="nenhum grupo encontrado" />
        <text-input v-if="user.role == 2" type="hidden" class="w-full lg:w-1/2" />

        <vue-select-input :getOptionLabel="(item) => item.user.name" v-model="item.professional" :options="professionalsFiltered" :error="item.errors[`professional.id`]" class="mt-6 lg:pr-6 w-full lg:w-1/2" label="Profissional" text="nenhum profissional encontrado" />
        <vue-select-input :getOptionLabel="(item) => item.user.name" v-model="item.patient" :options="patientsFiltered" :error="item.errors[`patient.id`]" class="mt-6 w-full lg:w-1/2" label="Paciente" text="nenhum paciente encontrado" />
        <date-time-input v-model="item.date_start" :error="item.errors.date_start" class="mt-6 lg:pr-6 w-full lg:w-1/2" label="Data e hora" />
        <mask-input mask="###" v-model="item.duration" :error="item.errors.duration" class="mt-6 w-full lg:w-1/2" label="Duração da consulta" placeholder="minutos" />
      </template>
    </modal-edit>
  </div>
</template>

<script>
import { Head, Link, useForm } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import ModalDelete from '@/Shared/ModalDelete'
import ModalEdit from '@/Shared/ModalEdit'
import DataTable from '@/Shared/DataTable'
import SwitchInput from '@/Shared/SwitchInput'
import TextInput from '@/Shared/TextInput'
import PasswordInput from '@/Shared/PasswordInput'
import MaskInput from '@/Shared/MaskInput'
import DateTimeInput from '@/Shared/DateTimeInput'
import Calendar from '@/Shared/Calendar'
import VueSelectInput from '@/Shared/VueSelectInput'
import { computed } from 'vue'
import { usePage } from '@inertiajs/inertia-vue3'
import format from 'date-fns/format';

export default {
  inject: ['openModalJitsi'],
  props: {
    totalDay: 0,
    totalMonth: 0,
    dottedDates: {
      type: Array,
      default: [],
    },
  },
  components: {
    Head,
    LoadingButton,
    ModalDelete,
    ModalEdit,
    DataTable,
    SwitchInput,
    TextInput,
    PasswordInput,
    MaskInput,
    DateTimeInput,
    VueSelectInput,
    format,
    Calendar,
  },
  layout: Layout,
  setup() {
    const user = computed(() => usePage().props.value.auth.user)
    return { user }
  },
  watch: {
    selectedDate(selectedDateNew, selectedDateOld) {
      if(selectedDateNew && selectedDateOld && format(selectedDateNew, 'yyyy-MM-dd') == format(selectedDateOld, 'yyyy-MM-dd')) {
        if(this.selectedDate) {
          this.monthYear = format(this.selectedDate, 'yyyy-MM');
        }
        this.selectedDate = null;
      } else {
        this.updateTotals(this.selectedDate ? format(this.selectedDate, 'yyyy-MM-dd') : null);
      }
      this.reloadTable();
    },
  },
  computed: {
    selectedDateFormatted: function() {
      return this.selectedDate ? format(this.selectedDate, 'yyyy-MM-dd') : '';
    },
    selectedMonthFormatted: function() {
      return this.monthYear ? this.monthYear : (this.selectedDate ? format(this.selectedDate, 'yyyy-MM') : '');
    },
    groupsFiltered: function() {
      let specialty_id = this.$refs.modalEdit?.item?.specialty?.id ?? null;
      let groupsAux = [];
      if(specialty_id) {
        this.professionals.forEach((p) => {
          if(p.specialty_id == specialty_id && groupsAux.indexOf(p.group_id) < 0) {
            groupsAux.push(p.group_id);
          }
        });
      }

      let professional_group_id = this.$refs.modalEdit?.item?.professional?.group_id ?? null;
      let patient_group_id = this.$refs.modalEdit?.item?.patient?.group_id ?? null;
      let groupsFiltered = this.groups.filter(function(p) {
        return (specialty_id == null || groupsAux.indexOf(p.id) >= 0) && (professional_group_id == null || p.id == professional_group_id) && (patient_group_id == null || p.id == patient_group_id);
      });
      let group_id = this.$refs.modalEdit?.item?.group?.id ?? null;
      if(group_id && !groupsFiltered.filter((p) => group_id == p.id).length) {
        this.$refs.modalEdit.item.group = null;
      }
      return groupsFiltered;
    },
    specialtiesFiltered: function() {
      let group_id = this.$refs.modalEdit?.item?.group?.id ?? null;
      let specialtiesAux = [];
      if(group_id) {
        this.professionals.forEach((p) => {
          if(p.group_id == group_id && specialtiesAux.indexOf(p.specialty_id) < 0) {
            specialtiesAux.push(p.specialty_id);
          }
        });
      }

      let professional_specialty_id = this.$refs.modalEdit?.item?.professional?.specialty_id ?? null;
      let specialtiesFiltered = this.specialties.filter(function(p) {
        return (group_id == null || specialtiesAux.indexOf(p.id) >= 0) && (professional_specialty_id == null || p.id == professional_specialty_id);
      });
      let specialty_id = this.$refs.modalEdit?.item?.specialty?.id ?? null;
      if(specialty_id && !specialtiesFiltered.filter((p) => specialty_id == p.id).length) {
        this.$refs.modalEdit.item.specialty = null;
      }
      return specialtiesFiltered;
    },
    professionalsFiltered: function() {
      let patient_group_id = this.$refs.modalEdit?.item?.patient?.group_id ?? null;
      let group_id = this.$refs.modalEdit?.item?.group?.id ?? null;
      let specialty_id = this.$refs.modalEdit?.item?.specialty?.id ?? null;
      let professionalsFiltered = this.professionals.filter(function(p) {
        return (patient_group_id == null || p.group_id == patient_group_id) && (group_id == null || p.group_id == group_id) && (specialty_id == null || p.specialty_id == specialty_id);
      });
      let professional_id = this.$refs.modalEdit?.item?.professional?.id ?? null;
      if(professional_id && !professionalsFiltered.filter((p) => professional_id == p.id).length) {
        this.$refs.modalEdit.item.professional = null;
      }
      return professionalsFiltered;
    },
    patientsFiltered: function() {
      let professional_group_id = this.$refs.modalEdit?.item?.professional?.group_id ?? null;
      let group_id = this.$refs.modalEdit?.item?.group?.id ?? null;
      let patientsFiltered = this.patients.filter(function(p) {
        return (professional_group_id == null || p.group_id == professional_group_id) && (group_id == null || p.group_id == group_id);
      });
      let patient_id = this.$refs.modalEdit?.item?.patient?.id ?? null;
      if(patient_id && !patientsFiltered.filter((p) => patient_id == p.id).length) {
        this.$refs.modalEdit.item.patient = null;
      }
      return patientsFiltered;
    },
  },
  mounted() {
    //this.selectedDate = new Date();
    this.loadOptions();
  },
  data() {
    return {
      specialties: [],
      groups: [],
      professionals: [],
      patients: [],
      item: {
        professional: {
          id: null,
          group_id: null,
          specialty_id: null,
          user: {
            id: null,
            name: null
          }
        },
        patient: {
          id: null,
          group_id: null,
          user: {
            id: null,
            name: null
          }
        },
        group: {
          id: null
        },
        specialty: {
          id: null,
          name: null,
        },
        errors: {}
      },
      monthYear: null,
      selectedDate: new Date(),
      loadTable: true,
      columns: [
        {
          uniqid: 'professional_name',
          label: 'Profissional',
          field: 'professional_user.name',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => `${row.professional.user.name}`,
        },
        {
          uniqid: 'patient_name',
          label: 'Paciente',
          field: 'patient_user.name',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => `${row.patient.user.name}`,
        },
        {
          uniqid: 'date_start',
          label: 'Data da Consulta',
          field: 'date_start',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => this.$filters.formatDateTime(row.date_start),
        },
        {
          uniqid: 'duration',
          label: 'Duração da consulta',
          field: 'duration',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => `${row.duration}`,
        },
        {
          uniqid: 'specialty',
          label: 'Especialidade',
          field: 'specialties.name',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => row.specialty ? row.specialty.name : ''
        },
        {
          uniqid: 'group',
          label: 'Grupo',
          field: 'groups.name',
          visible: usePage().props.value.auth.user.role == 1 ? true : false,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => row.group ? row.group.name : ''
        },
        {
          uniqid: 'actions',
          label: 'Ações',
          field: 'id',
          visible: true,
          align: 'center',
          classes: 'py-3 border-b',
          headerClass: 'p-4',
        },
      ],
    }
  },
  methods: {
    handleMonthYear({instance, month, year}) {
      let monthYear = year + '-' + (month + 1).toString().padStart(2, '0');
      this.monthYear = monthYear;
      this.updateDottedDates(monthYear + '-01', monthYear);
      this.reloadTable();
    },
    openModalConfirmDelete(item) {
      this.$refs.modalDelete.openModalDelete(item, `/appointments/${item.id}`, `Deseja realmente excluir o agendamento do paciente <span class="font-bold">${item.patient.user.name}</span> com o profissional <span class="font-bold">${item.professional.user.name}</span> em <span class="font-bold">` + this.$filters.formatDateTime(item.date_start) + `</span>?`);
    },
    openModalEdit(i) {
      this.item = {
        id: i.id,
        date_start: i.date_start,
        duration: i.duration,
        professional: i.id ? {} : null,
        patient: i.id ? {} : null,
        group: i.id ? {} : null,
        specialty: i.id ? {} : null,
      }
      if(i.professional && i.professional.user) {
        this.item.professional.id = i.professional.id;
        this.item.professional.user_id = i.professional.user_id;
        this.item.professional.group_id = i.professional.group_id;
        this.item.professional.specialty_id = i.professional.specialty_id;
        this.item.professional.user = {
          id: i.professional.user.id,
          name: i.professional.user.name,
        }
      }
      if(i.patient && i.patient.user) {
        this.item.patient.id = i.patient.id;
        this.item.patient.user_id = i.patient.user_id;
        this.item.patient.group_id = i.patient.group_id;
        this.item.patient.user = {
          id: i.patient.user.id,
          name: i.patient.user.name,
        }
      }
      if(this.user.role == 2) {
        this.item.group = {
          id: this.user.professional.group.id,
          name: this.user.professional.group.name,
        }
      } else {
        if(i.group) {
          this.item.group = {
            id: i.group.id,
            name: i.group.name,
          }
        }
      }
      if(i.specialty) {
        this.item.specialty = {
          id: i.specialty.id,
          name: i.specialty.name,
        }
      }
      this.$refs.modalEdit.openModalEdit(this.item);
    },
    reloadTable() {
      this.loadTable = false;
      setTimeout(() => this.loadTable = true, 10);
    },
    submitError() {
      this.updateTotals(this.selectedDate ? format(this.selectedDate, 'yyyy-MM-dd') : null);
    },
    updateDottedDates(date, yearMonth) {
      this.$inertia.reload({
        method: 'put',
        only: [
          'flash',
          'dottedDates',
          'totalMonth',
        ],
        data: {
          date: date,
          month: yearMonth,
        },
      });
    },
    updateTotals(date) {
      this.$inertia.reload({
        method: 'put',
        only: [
          'flash',
          'totalDay',
          'totalMonth',
          'dottedDates',
        ],
        data: {
          date: date,
          month: date ? date.substr(0, 7) : (this.monthYear ? this.monthYear : null),
        }
      });
    },
    async loadOptions() {
      await fetch(
        `appointments/get-options`
      ).then(res => {
        res.json().then(json => {
          this.specialties = json.specialties;
          this.groups = json.groups;
          this.professionals = json.professionals;
          this.patients = json.patients;
        });
      });
    },
    openRoom(item) {
      this.openModalJitsi(item);
    },
  },
}
</script>